import { AlertTriangle, Info } from 'lucide-react';
import { useSession } from '@/core/session';
import { useT } from '@/core/i18n';

/* =============================================================================
 * The database is behind the app
 * -----------------------------------------------------------------------------
 * A column this build reads does not exist yet, because a migration has not been
 * applied. The session recovered — it asked again for the columns that have
 * always been there — so the app runs, but every query that names one of the
 * newer columns still fails outright, and PostgREST fails the whole select
 * rather than the one column. On screen that is not a missing feature. It is
 * payroll with no rows in it, and job cost with no rows in it, and nothing
 * anywhere saying why.
 *
 * So it says why — because the failure it explains looks exactly like data
 * loss, and somebody who thinks they have lost a month of payroll does not go
 * looking in the console.
 *
 * -----------------------------------------------------------------------------
 * BUT NOT THE SAME THING TO EVERYBODY
 * -----------------------------------------------------------------------------
 * This used to put the migration command in front of every company user:
 * "run npm run db:pending, then paste supabase/setup/pending.sql into the SQL
 * editor". A company cannot do that, is not meant to know the platform works
 * that way, and read a red box about its own database as the system being
 * broken. Applying migrations is the platform's job, and the session has
 * already reported the condition to the platform — see session.tsx.
 *
 * So a company is told what is true for it — some screens may look empty for
 * a while, and nothing is lost — and the platform operator, looking at the same
 * app, is told what to run.
 * ========================================================================== */

export function PendingMigrations() {
  const { columnsMissing, platform } = useSession();
  const t = useT();

  if (!columnsMissing) return null;

  if (!platform) {
    return (
      <div
        role="status"
        className="rounded-card border border-line bg-surface-2 px-3.5 py-3 mb-4"
      >
        <div className="flex items-start gap-2.5">
          <Info size={17} className="text-info shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold">{t('pm.companyTitle')}</p>
            <p className="text-[12.5px] text-muted mt-1 leading-snug">
              {t('pm.companyBody')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="rounded-card border border-danger/40 bg-danger-subtle px-3.5 py-3 mb-4"
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle size={17} className="text-danger shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-danger">
            {t('pm.title')}
          </p>
          <p className="text-[12.5px] text-muted mt-1 leading-snug">
            {t('pm.body')}
          </p>
          <p className="text-[12px] text-subtle mt-1.5 leading-snug">
            {t('pm.how')}
          </p>
        </div>
      </div>
    </div>
  );
}
